import { ROUTES } from '@/constants/application-urls';
import { FOOTER } from '@/constants/footer';
import BrandDefinition from '@/types/BrandDefinition';
import FooterDictionary from '@/types/dictionaries/Footer';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/material/styles';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import LocalizedLink from '@/components/LocalizedLink';

const styles = {
	link: {
		fontWeight: 400,
		color: 'white.main',
		':hover': {
			fontWeight: 400,
			color: 'grey.light',
		},
	},
	box: {
		display: 'flex',
		alignItems: 'center',
		gap: '4px',
	},
	icon: {
		height: '18px',
		width: '18px',
		color: 'grey.light',
	},
} as const satisfies Record<string, SxProps>;

interface InformationSectionProps {
	dict: FooterDictionary;
	brand: BrandDefinition;
	showHeading: boolean;
}

export default function InformationSection({ dict, brand, showHeading }: Readonly<InformationSectionProps>) {
	const footerConstants = FOOTER[brand.brandCode];

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '8px' }} data-testid="footer-information">
			{showHeading && (
				<Typography variant="paragraphExtraBold" data-testid="footer-information-title">
					{dict.informationSection.title}
				</Typography>
			)}
			<Box sx={{ ...styles.box }} data-testid="footer-about-us-link">
				<Link target="_blank" href={footerConstants.aboutUsLink} sx={{ ...styles.link }}>
					{dict.informationSection.aboutUs}
				</Link>
				<OpenInNewOutlinedIcon sx={{ ...styles.icon }} />
			</Box>
			<Box sx={{ ...styles.box }} data-testid="footer-privacy-policy-link">
				<Link target="_blank" href={footerConstants.privacyPolicy} sx={{ ...styles.link }}>
					{dict.informationSection.privacyPolicy}
				</Link>
				<OpenInNewOutlinedIcon sx={{ ...styles.icon }} />
			</Box>
			<Box sx={{ ...styles.box }} data-testid="footer-terms-and-conditions">
				<Link
					{...(footerConstants.termsAndConditions.openInNewTab ? { target: '_blank' } : {})}
					href={footerConstants.termsAndConditions.url}
					sx={{ ...styles.link }}
				>
					{dict.informationSection.generalTermsAndConditions}
				</Link>
				{footerConstants.termsAndConditions.openInNewTab && (
					<OpenInNewOutlinedIcon data-testid="t&c-open-in-new-tab-icon" sx={{ ...styles.icon }} />
				)}
			</Box>
			<LocalizedLink href={ROUTES.FAQ} sx={{ ...styles.link }} data-testid="footer-faqs">
				{dict.informationSection.faq}
			</LocalizedLink>
		</Box>
	);
}
